import { toast } from "sonner";
import { Trash2, Palette } from "lucide-react";
import { formatINR } from "@/lib/quotationApi";

export default function TierRow({
  t,
  idx,
  count,
  finishes,
  modularMult,
  prices,
  onPatch,
  onMove,
  onRemove,
  onSetFinishPrice,
  onFillAll,
  onClearAll,
}) {
  const applyPrefill = () => {
    const pct = parseFloat(t._prefillPct);
    if (!Number.isFinite(pct) || pct < 0 || pct >= 100) {
      toast.error("Enter a discount between 0 and 99");
      return;
    }
    onFillAll(t.id, pct);
    toast.success(`Filled ${finishes.length} finishes at ${pct}% off`);
  };

  return (
    <div
      className="rounded-xl border p-3 space-y-3"
      style={{
        borderColor: `${t.color || "#C5A880"}55`,
        background: `${t.color || "#C5A880"}0A`,
      }}
      data-testid={`tier-row-${idx}`}
    >
      <div className="flex items-center gap-2">
        <div
          className="w-4 h-4 rounded-sm border border-white/20 shrink-0"
          style={{ background: t.color || "#C5A880" }}
        />
        <input
          className="input-luxe !h-9 !py-1 flex-1 font-display font-semibold"
          value={t.name || ""}
          onChange={(e) => onPatch({ name: e.target.value })}
          placeholder="Tier name"
          maxLength={40}
          data-testid={`tier-name-input-${idx}`}
        />
        {t.is_base && (
          <span className="text-[10px] px-2 py-1 rounded-full bg-[#C5A880]/10 border border-[#C5A880]/40 text-[#C5A880] uppercase tracking-wider">
            Base
          </span>
        )}
        <div className="flex items-center gap-1">
          <button
            onClick={() => onMove(-1)}
            disabled={idx === 0}
            className="p-1.5 rounded text-white/60 hover:text-white disabled:opacity-30"
            title="Move up"
            data-testid={`tier-move-up-${idx}`}
          >
            ▲
          </button>
          <button
            onClick={() => onMove(1)}
            disabled={idx === count - 1}
            className="p-1.5 rounded text-white/60 hover:text-white disabled:opacity-30"
            title="Move down"
            data-testid={`tier-move-down-${idx}`}
          >
            ▼
          </button>
          {!t.is_base && (
            <button
              onClick={onRemove}
              className="p-1.5 rounded text-red-300/70 hover:text-red-300"
              title="Remove"
              data-testid={`tier-remove-${idx}`}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      <textarea
        className="input-luxe !h-16 !py-2 leading-snug resize-none"
        value={t.description || ""}
        onChange={(e) => onPatch({ description: e.target.value })}
        placeholder="Short description shown to the customer"
        maxLength={240}
        data-testid={`tier-desc-input-${idx}`}
      />

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-luxe flex items-center gap-1.5">
            <Palette size={11} /> Accent colour
          </label>
          <div className="flex gap-2">
            <input
              type="color"
              value={(t.color || "#C5A880").slice(0, 7)}
              onChange={(e) => onPatch({ color: e.target.value.toUpperCase() })}
              className="w-12 h-11 rounded-lg bg-transparent border border-white/10 cursor-pointer p-1"
              data-testid={`tier-color-${idx}`}
            />
            <input
              className="input-luxe uppercase tracking-widest flex-1"
              value={t.color || "#C5A880"}
              onChange={(e) => onPatch({ color: e.target.value.toUpperCase() })}
              maxLength={7}
              data-testid={`tier-color-hex-${idx}`}
            />
          </div>
        </div>
        <div>
          <label className="label-luxe">Modular × (override)</label>
          <input
            type="number"
            step="0.1"
            inputMode="decimal"
            className="input-luxe"
            value={t.modular_multiplier ?? ""}
            onChange={(e) => onPatch({ modular_multiplier: e.target.value })}
            placeholder={`Default ${modularMult}`}
            data-testid={`tier-mm-${idx}`}
          />
        </div>
      </div>

      {/* Per-finish prices at this tier */}
      {!t.is_base && (
        <div className="pt-1">
          <div className="overline text-white/55 mb-1.5">
            Finish prices at this tier (₹/sqft)
          </div>
          <p className="text-[11px] text-white/40 mb-2 leading-snug">
            Type the actual rupee rate per finish, or use &quot;Quick prefill&quot; below to apply a % off base across all finishes in one tap. Leave blank to keep the base price for that finish.
          </p>
          {finishes.length > 0 && (
            <div className="flex items-end gap-2 mb-2 rounded-lg border border-[#C5A880]/20 bg-[#C5A880]/5 p-2">
              <div className="flex-1">
                <label className="text-[10px] uppercase tracking-wider text-white/50">
                  Quick prefill · % off base
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  className="input-luxe !h-9"
                  placeholder="e.g. 15"
                  value={t._prefillPct ?? ""}
                  onChange={(e) => onPatch({ _prefillPct: e.target.value })}
                  data-testid={`tier-${idx}-prefill-pct`}
                />
              </div>
              <button
                type="button"
                className="btn-ghost !h-9 whitespace-nowrap !px-3 text-[12px]"
                onClick={applyPrefill}
                data-testid={`tier-${idx}-prefill-apply`}
              >
                Fill all
              </button>
              <button
                type="button"
                className="btn-ghost !h-9 whitespace-nowrap !px-2 text-[11px] text-white/55"
                onClick={() => onClearAll(t.id)}
                data-testid={`tier-${idx}-prefill-clear`}
              >
                Clear
              </button>
            </div>
          )}
          {finishes.length === 0 ? (
            <div className="text-[12px] text-white/35 py-2">
              No finishes in the master list yet.
            </div>
          ) : (
            <div className="space-y-1.5 rounded-lg border border-white/5 bg-black/20 p-2">
              {finishes.map((f) => (
                <div key={f.id} className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] truncate">{f.name}</div>
                    <div className="text-[10px] text-white/35">
                      base {formatINR(f.price_per_sqft)}/sqft
                    </div>
                  </div>
                  <input
                    type="number"
                    inputMode="decimal"
                    className="input-luxe !h-9 !w-28 !text-right"
                    placeholder="base"
                    value={prices?.[f.id] ?? ""}
                    onChange={(e) => onSetFinishPrice(t.id, f.id, e.target.value)}
                    data-testid={`tier-${idx}-finish-price-${f.id}`}
                  />
                  <span className="text-[10px] text-white/30 w-8 text-right">/sqft</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
